import React, { useMemo } from 'react';
import { useAtom } from 'jotai';
import { focusAtom } from 'jotai/optics';
import { css } from '@linaria/core';
import { styled } from '@linaria/react';
import useTranslation from 'next-translate/useTranslation';
import {
  Card,
  FormControl,
  FormLabel,
  Modal,
  Stack,
  Text,
  breakpoints,
} from '@spaceshipapp/cambridge';
import Button from '../../../components/Button';
import Link from '../../../components/Link';
import { parcelMapAtom, parcelItemsAtom, itemMapAtom } from '../../../contexts/shipment';
import { prohibitedHsCodeMapAtom } from '../../../contexts/quotation';
import SizePicker from '../SizePicker';
import { getMatchedPresetSize, getDisplayWeight } from '../utils';
import { ParcelPresetId } from '../../../common/package';
import SelectButton from '../../../components/Select/SelectButton';
import { ShipmentType } from '../../../types/common';
import Items from '../Items';
import RemoveWarning from './RemoveWarning';
import useYup from '../../../hooks/useYup';

interface Props {
  parcelId: string;
  allowRemove: boolean;
  onRemove(): void;
  error?: string | ReturnType<typeof useYup>['errors'];
}

const Parcel: React.FC<Props> = (props) => {
  const { t } = useTranslation('common');
  const { parcelId, allowRemove, onRemove, error } = props;
  const parcelAtom = useMemo(
    () => focusAtom(parcelMapAtom, (o) => o.prop(parcelId)),
    [parcelMapAtom],
  );
  const itemsAtom = useMemo(
    () => focusAtom(parcelItemsAtom, (o) => o.prop(parcelId)),
    [parcelItemsAtom],
  );
  const [parcel] = useAtom(parcelAtom);
  const [itemIds] = useAtom(itemsAtom);
  const [itemMap] = useAtom(itemMapAtom);
  const [prohibitedHsCodeMap] = useAtom(prohibitedHsCodeMapAtom);
  const { length, width, height } = parcel || {};
  const prohibitedCount = (itemIds || [])
    .map((item) => itemMap[item])
    .map(({ hkhsCode }) => !!(hkhsCode && prohibitedHsCodeMap[hkhsCode]))
    .filter(Boolean).length;

  const preset = getMatchedPresetSize(parcel, ShipmentType.PARCEL) as ParcelPresetId;

  const description: Record<ParcelPresetId, string> = {
    small: t('Parcel.parcelSmall'),
    medium: t('Parcel.parcelMedium'),
    large: t('Parcel.parcelLarge'),
  };

  const descriptionEn: Record<ParcelPresetId, string> = {
    small: t('commonEn:Parcel.parcelSmall'),
    medium: t('commonEn:Parcel.parcelMedium'),
    large: t('commonEn:Parcel.parcelLarge'),
  };

  const errorMessage =
    (error && t(error as string)) ||
    (prohibitedCount ? (
      <Stack gap="md" as="span">
        <Text as="span">{t('Items.prohibitedDetected', { count: prohibitedCount })}</Text>
        <Link href={t('URL.prohibitedItems')}>{t('Items.prohibitedHelp')}</Link>
      </Stack>
    ) : undefined);

  if (!parcel) return null;

  return (
    <Card className={cardStyle} errorMessage={errorMessage}>
      <Stack>
        <FormControl>
          <FormLabel>{t('Parcel.formLabelParcelSize')}</FormLabel>
          <Modal
            title={t('Parcel.sizePickerTitle')}
            className={modalStyle}
            trigger={
              <SelectButton
                className={selectStyle}
                data-tracking-label={t('commonEn:Parcel.buttonlabel', {
                  parcelSize: preset ? descriptionEn[preset] : t('commonEn:Parcel.parcelCustom'),
                  length,
                  width,
                  height,
                  weight: getDisplayWeight(parcel, ShipmentType.PARCEL),
                })}
              >
                {t('Parcel.buttonlabel', {
                  parcelSize: preset ? description[preset] : t('Parcel.parcelCustom'),
                  length,
                  width,
                  height,
                  weight: getDisplayWeight(parcel, ShipmentType.PARCEL),
                })}
              </SelectButton>
            }
          >
            <SizePicker parcelId={parcelId} />
          </Modal>
        </FormControl>
        <Items parcelId={parcelId} />
      </Stack>
      {allowRemove && (
        <Wrapper>
          <Modal
            title={t('Parcel.modalWarningTitle')}
            trigger={
              <Button
                size="xs"
                iconName="Minus"
                className={removeBtnStyle}
                aria-label={t('Parcel.btnRemove')}
                data-tracking-label={t('commonEn:Parcel.btnRemove')}
              />
            }
          >
            <RemoveWarning onRemove={onRemove} />
          </Modal>
        </Wrapper>
      )}
    </Card>
  );
};

const cardStyle = css`
  position: relative;
`;

const selectStyle = css`
  width: 100%;
`;

const Wrapper = styled.div`
  position: absolute;
  top: calc(50% - 11px); // TODO: fix hardcoded value
  right: -11px;
`;

const removeBtnStyle = css`
  border-radius: 100%;

  &:before {
    border-radius: 100%;
  }
`;

const modalStyle = css`
  @media (min-width: ${breakpoints.md}) {
    max-width: 28rem;
  }
`;

export default Parcel;
